import { useMemo, useState } from 'react';

import { NULL_FILTER_VALUE } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { PopoverArrow } from '@radix-ui/react-popover';
import { useQuery } from '@tanstack/react-query';
import { cva } from 'class-variance-authority';
import {
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  endOfYear,
  isSameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from 'date-fns';
import { MultiValue } from 'react-select';
import { match } from 'ts-pattern';

import FilterIcon from '@/components/icons/filter';
import UndoIcon from '@/components/icons/undo';
import { Input } from '@/components/ui/input';
import { AsyncMultiSelect, MultiSelect } from '@/components/ui/multi-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { getColumnFilterOptionsQueryOptions } from '@/queries/column-filter';

import { DatePicker } from '../ui/date-picker';
import { Label } from '../ui/label';

const NULL_FILTER_OPTION = { label: '-', value: NULL_FILTER_VALUE } as const;

const columnFilterButtonOverrides = cva(
  'w-full gap-1 p-2 font-medium disabled:!pointer-events-auto disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-transparent',
);

type ColumnFilterPopoverBaseProps = {
  columnId: string;
  onRemove: (columnId: string) => void;
  /** When true, the "-" (null-filter) option is not injected for this column. */
  disableNullFilter?: boolean;
};

type ColumnFilterPopoverFieldProps<T, U> = {
  type: T;
  value?: U;
  onApply: (columnId: string, value?: U) => void;
};

type ColumnFilterPopoverGetFieldWithGetFilterOptions<T, U> = ColumnFilterPopoverFieldProps<T, U> & {
  getFilterOptions?: (inputValue?: string | undefined, value?: string | string[] | undefined) => Promise<{ label: string; value: string }[]>;
};

type ColumnFilterPopoverDateRangeProps = ColumnFilterPopoverFieldProps<'date-range', [Date, Date]> & {
  title?: string;
};

export type ColumnFilterPopoverProps = ColumnFilterPopoverBaseProps &
  (
    | ColumnFilterPopoverDateRangeProps
    | ColumnFilterPopoverFieldProps<'text', string>
    | ColumnFilterPopoverGetFieldWithGetFilterOptions<'multi-select', string[]>
    | ColumnFilterPopoverGetFieldWithGetFilterOptions<'async-multi-select', string[]>
  );

export function ColumnFilterPopover(props: ColumnFilterPopoverProps) {
  const { columnId, type, value, onApply, onRemove, disableNullFilter } = props;
  const getFilterOptions = 'getFilterOptions' in props ? props.getFilterOptions : undefined;
  const title = 'title' in props ? props.title : undefined;

  const [open, setOpen] = useState(false);
  const [filterValue, setFilterValue] = useState(value);
  const [selectedPreset, setSelectedPreset] = useState<string>('');

  function handleApply() {
    if (type === 'date-range') {
      onApply(columnId, filterValue as [Date, Date]);
    } else if (type === 'text') {
      onApply(columnId, filterValue as string);
    } else if (type === 'multi-select') {
      onApply(columnId, filterValue as string[]);
    } else if (type === 'async-multi-select') {
      onApply(columnId, filterValue as string[]);
    }
    setOpen(false);
  }

  function handleRemove() {
    setFilterValue(undefined);
    setSelectedPreset('');
    onRemove(columnId);
    setOpen(false);
  }

  function handleTextInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    setFilterValue(event.target.value);
  }

  function handleSelectChange(value: MultiValue<{ label: string; value: string }>) {
    setFilterValue(value.map((option) => option.value));
  }

  function handleOnOpenChange(open: boolean) {
    setOpen(open);
    if (open) {
      setFilterValue(value);
      if (type === 'date-range') {
        updateSelectedPresetFromValue(value as [Date, Date] | undefined);
      } else {
        setSelectedPreset('');
      }
    }
  }

  function handlePresetChange(preset: string) {
    setSelectedPreset(preset);
    const dateRange = getDateRange(preset);
    setFilterValue(dateRange);
  }

  function findMatchingPreset(dateRange: [Date, Date] | undefined): string {
    if (!dateRange) return '';

    const [start, end] = dateRange;

    for (const preset of DATE_PRESETS) {
      const presetRange = getDateRange(preset.value);
      if (isSameDay(start, presetRange[0]) && isSameDay(end, presetRange[1])) {
        return preset.value;
      }
    }

    return '';
  }

  function updateSelectedPresetFromValue(dateRange: [Date, Date] | undefined) {
    const matchingPreset = findMatchingPreset(dateRange);
    setSelectedPreset(matchingPreset);
  }

  const { data: options, isLoading: isLoadingOptions } = useQuery(getColumnFilterOptionsQueryOptions({ columnId, filterValue, getFilterOptions }));

  function renderFilterInput() {
    return match(type)
      .with('text', () => <Input placeholder="Text..." value={(filterValue as string) || ''} onChange={handleTextInputChange} />)
      .with('multi-select', () => {
        const baseOptions = options ?? [];
        const optionsWithNull = disableNullFilter ? baseOptions : [NULL_FILTER_OPTION, ...baseOptions];
        const displayOptions = options ? optionsWithNull : undefined;
        return (
          <MultiSelect<{ label: string; value: string }>
            placeholder="Select or search..."
            value={displayOptions?.filter((option) => (filterValue as string[])?.includes(option.value)) || undefined}
            onChange={handleSelectChange}
            options={displayOptions}
          />
        );
      })
      .with('async-multi-select', () => {
        if (isLoadingOptions || !options) {
          return <div className="bg-grey-2 h-10 w-full animate-pulse rounded-md" />;
        }

        const optionsWithNull = disableNullFilter ? options : [NULL_FILTER_OPTION, ...options];

        return (
          <AsyncMultiSelect<{ label: string; value: string }>
            placeholder="Select or search..."
            defaultOptions
            loadOptions={async (input: string) => {
              const updatedOptions = await getFilterOptions?.(input);
              const converted = updatedOptions
                ? updatedOptions.map((option) => ({
                    label: option.label,
                    value: Array.isArray(option.value) ? option.value[0] : option.value,
                  }))
                : [];
              // Text-search filters the real options only; the null sentinel is
              // prepended after so it stays visible while the user types (its '-'
              // label would never match a search term).
              const filtered = converted.filter(({ label }) => label.toLowerCase().includes(input.toLowerCase()));
              return disableNullFilter ? filtered : [NULL_FILTER_OPTION, ...filtered];
            }}
            defaultValue={optionsWithNull.filter((option) => (filterValue as string[])?.includes(option.value)) || undefined}
            onChange={handleSelectChange}
          />
        );
      })
      .with('date-range', () => {
        const dateRangeValue = filterValue as [Date, Date] | undefined;
        return (
          <div className="flex w-full flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Label className="text-xs">From</Label>
                <DatePicker
                  date={dateRangeValue?.[0]}
                  setDate={(date) => {
                    const normalizedDate = date ? startOfDay(date) : undefined;
                    const newRange: [Date, Date] | undefined = normalizedDate ? [normalizedDate, dateRangeValue?.[1] || endOfDay(normalizedDate)] : undefined;
                    setFilterValue(newRange);
                    updateSelectedPresetFromValue(newRange);
                  }}
                  placeholder="dd/mm/yyyy"
                />
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">To</Label>
                <DatePicker
                  date={dateRangeValue?.[1]}
                  setDate={(date) => {
                    const normalizedDate = date ? endOfDay(date) : undefined;
                    const newRange: [Date, Date] | undefined = normalizedDate ? [dateRangeValue?.[0] || startOfDay(normalizedDate), normalizedDate] : undefined;
                    setFilterValue(newRange);
                    updateSelectedPresetFromValue(newRange);
                  }}
                  placeholder="dd/mm/yyyy"
                />
              </div>
            </div>

            <hr />

            <div className="flex flex-col gap-2">
              {title && <Label className="text-xs font-medium">{title}</Label>}
              <Select<{ label: string; value: string }>
                value={DATE_PRESETS.find((preset) => preset.value === selectedPreset) ?? null}
                onChange={(option) => option && handlePresetChange(option.value)}
                options={DATE_PRESETS}
                placeholder="Select..."
                isClearable={false}
              />
            </div>

            <div className="-mt-2" />
          </div>
        );
      })
      .exhaustive();
  }

  const filterApplied = useMemo(() => (Array.isArray(value) ? value.length > 0 : value), [value]);

  return (
    <Popover open={open} onOpenChange={handleOnOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Filter column"
          className={cn(
            'hover:bg-default-light-blue hover:text-default-blue data-[state=open]:bg-default-light-blue data-[state=open]:text-default-blue p-0',
            filterApplied && 'bg-default-light-blue text-default-blue',
          )}>
          <FilterIcon />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex w-[280px] flex-col gap-2 p-2">
        <PopoverArrow className="fill-white" />
        {renderFilterInput()}
        <hr />
        <div className="flex w-full gap-2">
          <Button variant="ghost" className={columnFilterButtonOverrides()} disabled={!filterApplied} onClick={handleRemove}>
            <UndoIcon />
            Remove filter
          </Button>
          <Button variant="ghost" className={columnFilterButtonOverrides()} onClick={handleApply}>
            <FilterIcon />
            Apply filter
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const DATE_PRESETS = [
  { label: 'Today', value: 'today' },
  { label: 'Current Week', value: 'current-week' },
  { label: 'Next Week', value: 'next-week' },
  { label: 'Current Month', value: 'current-month' },
  { label: 'Current Year', value: 'current-year' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Past 7 Days', value: 'past-7-days' },
  { label: 'Past 30 Days', value: 'past-30-days' },
  { label: 'Last Calendar Week', value: 'last-calendar-week' },
  { label: 'Last Calendar Month', value: 'last-calendar-month' },
  { label: 'Last Calendar Year', value: 'last-calendar-year' },
] as const;

function getDateRange(preset: string): [Date, Date] {
  const today = new Date();

  return match(preset)
    .with('today', (): [Date, Date] => [startOfDay(today), endOfDay(today)])
    .with('yesterday', (): [Date, Date] => [subDays(startOfDay(today), 1), subDays(endOfDay(today), 1)])
    .with('current-week', (): [Date, Date] => [startOfWeek(startOfDay(today)), endOfWeek(endOfDay(today))])
    .with('next-week', (): [Date, Date] => [startOfWeek(addWeeks(startOfDay(today), 1)), endOfWeek(addWeeks(endOfDay(today), 1))])
    .with('current-month', (): [Date, Date] => [startOfMonth(startOfDay(today)), endOfMonth(endOfDay(today))])
    .with('current-year', (): [Date, Date] => [startOfYear(startOfDay(today)), endOfYear(endOfDay(today))])
    .with('past-7-days', (): [Date, Date] => [subDays(startOfDay(today), 7), endOfDay(today)])
    .with('past-30-days', (): [Date, Date] => [subDays(startOfDay(today), 30), endOfDay(today)])
    .with('last-calendar-week', (): [Date, Date] => [startOfWeek(subWeeks(startOfDay(today), 1)), endOfWeek(subWeeks(endOfDay(today), 1))])
    .with('last-calendar-month', (): [Date, Date] => [startOfMonth(subMonths(startOfDay(today), 1)), endOfMonth(subMonths(endOfDay(today), 1))])
    .with('last-calendar-year', (): [Date, Date] => [startOfYear(subYears(startOfDay(today), 1)), endOfYear(subYears(endOfDay(today), 1))])
    .otherwise((): [Date, Date] => [startOfDay(today), endOfDay(today)]);
}
